import { rhybusRListsHttp } from '../api/rhybus-list-http'
import { rhybusWhoHttp } from '../api/rhybus-who-http'
import { ListNames } from '../config/configuration'
import { sendNotification } from './notificationUtils'

// const selectColumns = NOTIFICATION_SELECT_COLUMNS.filter(col => !col.startsWith('Read/')).join(',')
type UserManagementNotificationAction = 'add' | 'delete'

const formatNotificationDate = (value: any): string => {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return String(value)
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const yyyy = d.getFullYear()
  const hh = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${mm}/${dd}/${yyyy} ${hh}:${min}`
}

export const getUserTitle = async (loginName: string, fallbackName: string): Promise<string> => {
  try {
    const matches = [...(await rhybusWhoHttp().Picker(loginName))]
    return matches[0]?.displayName || fallbackName
  } catch (error) {
    console.error('Error resolving user title:', error)
    return fallbackName
  }
}

export const sendUserManagementNotification = async (
  action: UserManagementNotificationAction,
  userName: string
): Promise<void> => {
  const isAdd = action === 'add'
  const activity = isAdd ? 'User Added' : 'User Deleted'
  const subject = `User ${userName} has been ${isAdd ? 'added' : 'removed'} successfully`

  await sendNotification({
    title: activity,
    activity,
    subject,
    notificationType: 'Notification',
    status: 'Success'
  })
}

export const fetchAllNotifications = async (): Promise<any[]> => {
  try {
    const me = await rhybusWhoHttp().Me()
    const currentUserEmail: string = (me?.email ?? me?.upn ?? '').toLowerCase()

    const list = await rhybusRListsHttp().getList(ListNames.NOTIFICATION)
    const query = list.query().orderBy([{ orderBy: 'ItemModified', ascending: false }])
    const items: any[] = [...(await query.get<any>())]

    // Show notifications addressed to this user, or broadcast notifications (no ToUser set)
    const filtered = items.filter((item: any) => {
      const toUser = (item.ToUser ?? '').toLowerCase()
      return !toUser || toUser === currentUserEmail
    })

   // return filtered.map(item => ({
   return items.map(item => ({
      ...item,
      ItemID: item.ItemGUID,
      id: item.ItemGUID,
      ItemCreated: formatNotificationDate(item.ItemCreated),
      ItemModified: formatNotificationDate(item.ItemModified),
    }))
  } catch (error) {
    console.error('Error fetching notifications:', error)
    return []
  }
}

export const markNotificationAsRead = async (eKey: string, eTag: string): Promise<void> => {
  try {
    const list = await rhybusRListsHttp().getList(ListNames.NOTIFICATION)
    await list.addOrUpdateItem({ eKey, eTag, IsRead: true })
  } catch (error) {
    console.error('Error marking notification as read:', error)
  }
}

export const markAllNotificationsRead = async (items: Array<{ eKey: string; eTag: string }>): Promise<void> => {
  try {
    const list = await rhybusRListsHttp().getList(ListNames.NOTIFICATION)
    await Promise.all(
      items.map(({ eKey, eTag }) => list.addOrUpdateItem({ eKey, eTag, IsRead: true }))
    )
  } catch (error) {
    console.error('Error marking all notifications as read:', error)
  }
}

export const handleClearAllNotifications = async () => {
  const [{ default: store }, { clearAllNotifications }] = await Promise.all([
    import('../store'),
    import('./notificationsSlice'),
  ])
  store.dispatch(clearAllNotifications())
}

export const navigateToDetailedview = (item: any) => {
  if (item?.Activity === 'Feedback Submit') {
    window.location.hash = '/settings?tab=site-feedback'
  } else {
    const guid = item?.InquiryGUID || item?.ItemID
    if (guid) {
      const subject = String(item?.Subject ?? '').toLowerCase()
      const isCase = subject.includes('grant case')
      const isTraining = subject.includes('training track')
      if (isCase) {
        window.location.hash = `/detail-view/${guid}`
      } else if (isTraining) {
        window.location.hash = `/training-detail-view/${guid}`
      }
    }
  }
}

export const onMarkAllRead = async () => {
  const [{ default: store }, { markAllRead }] = await Promise.all([
    import('../store'),
    import('./notificationsSlice'),
  ])
  const unreadItems = store.getState().notifications.items
    .filter((item: any) => !item.IsRead && item.eKey)
    .map((item: any) => ({ eKey: item.eKey, eTag: item.eTag }))
  store.dispatch(markAllRead())
  await markAllNotificationsRead(unreadItems)
}

export const onNotificationItemRead = async (item: any) => {
  if (!item?.IsRead) {
    const [{ default: store }, { markOneRead }] = await Promise.all([
      import('../store'),
      import('./notificationsSlice'),
    ])
    store.dispatch(markOneRead(item.ItemID))
    if (item?.eKey) {
      await markNotificationAsRead(item.eKey, item.eTag)
    }
  }
}
